import React, { useState, useRef, useCallback, useMemo } from 'react';
import { Box, IconButton, InputBase, Typography, Menu, MenuItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RepeatIcon from '@mui/icons-material/Repeat';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import WysiwygFieldBlock from './WysiwygFieldBlock';
import { makeDefaultField, FIELD_CATEGORIES } from './fieldTypes';
import { SettingsInput, SettingsRow, SettingsToggle } from './SettingsPrimitives';
import { TipTapEditor } from '../../../TipTap';

/* ─── Child field categories (exclude repeatable_section to prevent nesting) ─── */

const CHILD_FIELD_CATEGORIES = FIELD_CATEGORIES
  .map((cat) => ({
    ...cat,
    types: cat.types.filter((t) => t.value !== 'repeatable_section'),
  }))
  .filter((cat) => cat.types.length > 0);

/* ─── Main repeatable section block ─── */

export default function RepeatableSectionBlock({
  field,
  index,
  total,
  sortableId,
  onUpdate,
  onRemove,
  onMove,
  onDuplicate,
  onAddField,
  onUpdateChildField,
  onAddChildField,
  onRemoveChildField,
  onDuplicateChildField,
  onMoveChildField,
  theme,
  palette,
  availablePlaceholders,
}) {
  const [settingsExpanded, setSettingsExpanded] = useState(false);
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  const [childAddMenuAnchor, setChildAddMenuAnchor] = useState(null);
  const [hovered, setHovered] = useState(false);
  const leaveTimerRef = useRef(null);
  const addMenuOpen = Boolean(addMenuAnchor);
  const childAddMenuOpen = Boolean(childAddMenuAnchor);

  const children = field.children || [];

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sortableId || field._id || field.key });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
  };

  /* ─── Child DnD ─── */

  const childSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const childSortableIds = useMemo(
    () => children.map((c, i) => c._id || `child_${i}`),
    [children]
  );

  const handleChildDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const findChild = (id) => children.findIndex((c, i) => (c._id || `child_${i}`) === id);
      const oldIdx = findChild(active.id);
      const newIdx = findChild(over.id);
      if (oldIdx === -1 || newIdx === -1) return;
      onMoveChildField(index, oldIdx, newIdx);
    },
    [children, index, onMoveChildField]
  );

  /* ─── Event handlers ─── */

  const handleFieldUpdate = (updates) => onUpdate(index, updates);

  const handleAddSelect = (type) => {
    onAddField(type, index + 1);
    setAddMenuAnchor(null);
  };

  const handleChildAddSelect = (type) => {
    onAddChildField(index, type, children.length);
    setChildAddMenuAnchor(null);
  };

  const handleMouseEnter = useCallback(() => {
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
    setHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    leaveTimerRef.current = setTimeout(() => {
      setHovered(false);
    }, 300);
  }, []);

  const showControls = hovered || isDragging || settingsExpanded || addMenuOpen;

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...attributes}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        opacity: isDragging ? 0.5 : 1,
        overflow: 'visible',
        cursor: 'default',
      }}
    >
      {/* Drag handle */}
      <Box
        {...listeners}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          py: '1px',
          cursor: 'grab',
          color: palette.textSecondary,
          opacity: showControls ? 1 : 0,
          transition: 'opacity 0.15s ease',
          '&:active': { cursor: 'grabbing' },
        }}
      >
        <DragIndicatorIcon sx={{ width: 16, height: 12, transform: 'rotate(90deg)' }} />
      </Box>

      {/* Section card */}
      <Box
        sx={{
          border: `1px solid ${showControls ? palette.textSecondary : palette.border}`,
          borderRadius: '10px',
          backgroundColor: palette.surface,
          overflow: 'hidden',
          transition: 'border-color 120ms ease, background-color 120ms ease, box-shadow 120ms ease',
          ...(showControls && { boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }),
        }}
      >
        {/* Section header */}
        <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <RepeatIcon sx={{ width: 16, height: 16, color: palette.textSecondary }} />
            <Box sx={{ flex: 1, minWidth: 0, fontSize: '12px', fontWeight: 600, color: palette.textPrimary }}>
              <TipTapEditor
                value={field.label || ''}
                onChange={(val) => handleFieldUpdate({ label: val })}
                showToolbar={false}
                stripHtml={true}
                availablePlaceholders={availablePlaceholders}
                placeholder="Section label..."
                minHeight="auto"
                className="tiptap-inline"
              />
            </Box>
            {field.required && (
              <Typography sx={{ fontSize: '11px', color: palette.errorText || '#b91c1c', flexShrink: 0 }}>*</Typography>
            )}
          </Box>

          {/* Child fields drop zone */}
          <Box
            sx={{
              border: `2px dashed ${palette.border}`,
              borderRadius: '8px',
              backgroundColor: palette.surfaceSubtle,
              p: children.length > 0 ? 1 : 0,
              minHeight: children.length === 0 ? 60 : undefined,
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              position: 'relative',
            }}
          >
            {children.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 60,
                  gap: '4px',
                }}
              >
                <Typography sx={{ fontSize: '11px', color: palette.textSecondary, opacity: 0.7 }}>
                  No fields in this section
                </Typography>
                <Box
                  onClick={(e) => { e.stopPropagation(); setChildAddMenuAnchor(e.currentTarget); }}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '3px',
                    px: '8px',
                    py: '3px',
                    borderRadius: '100px',
                    border: `1px dashed ${palette.border}`,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    userSelect: 'none',
                    '&:hover': { borderColor: palette.textSecondary, borderStyle: 'solid' },
                  }}
                >
                  <AddIcon sx={{ width: 13, height: 13, color: palette.textSecondary }} />
                  <Typography sx={{ fontSize: '11px', fontWeight: 500, color: palette.textSecondary }}>
                    Add field
                  </Typography>
                </Box>
              </Box>
            ) : (
              <DndContext
                sensors={childSensors}
                collisionDetection={closestCenter}
                onDragEnd={handleChildDragEnd}
              >
                <SortableContext items={childSortableIds} strategy={verticalListSortingStrategy}>
                  {children.map((child, childIdx) => {
                    const childStableId = child._id || `child_${childIdx}`;
                    return (
                    <WysiwygFieldBlock
                      key={childStableId}
                      sortableId={childStableId}
                      field={child}
                      index={childIdx}
                      total={children.length}
                      onUpdate={(ci, updates) => onUpdateChildField(index, ci, updates)}
                      onRemove={(ci) => onRemoveChildField(index, ci)}
                      onMove={(from, to) => onMoveChildField(index, from, to)}
                      onDuplicate={(ci) => onDuplicateChildField(index, ci)}
                      onAddField={(type, pos) => onAddChildField(index, type, pos)}
                      theme={theme}
                      palette={palette}
                      availablePlaceholders={availablePlaceholders}
                    />
                    );
                  })}
                </SortableContext>
              </DndContext>
            )}
          </Box>

          {/* Add child field button (shown when there are already children) */}
          {children.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: '2px' }}>
              <Box
                onClick={(e) => { e.stopPropagation(); setChildAddMenuAnchor(e.currentTarget); }}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '3px',
                  px: '8px',
                  py: '3px',
                  borderRadius: '100px',
                  border: `1px dashed ${palette.border}`,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  userSelect: 'none',
                  '&:hover': { borderColor: palette.textSecondary, borderStyle: 'solid' },
                }}
              >
                <AddIcon sx={{ width: 13, height: 13, color: palette.textSecondary }} />
                <Typography sx={{ fontSize: '11px', fontWeight: 500, color: palette.textSecondary }}>
                  Add field to section
                </Typography>
              </Box>
            </Box>
          )}

          {field.helpText && (
            <Typography sx={{ fontSize: '11px', color: palette.textSecondary, mt: 0.25 }}>
              {field.helpText}
            </Typography>
          )}
        </Box>

        {/* Settings panel */}
        {settingsExpanded && (
          <Box
            sx={{
              borderTop: `1px solid ${palette.border}`,
              backgroundColor: palette.surfaceSubtle,
              px: 1.25,
              py: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <SettingsInput
              label="Section key"
              value={field.key}
              onChange={(val) => onUpdate(index, { key: val.replace(/\s+/g, '_').toLowerCase() })}
              placeholder="section_key"
              mono
              palette={palette}
              theme={theme}
            />
            <SettingsInput
              label="Help text"
              value={field.helpText || ''}
              onChange={(val) => onUpdate(index, { helpText: val })}
              placeholder="Optional help text"
              palette={palette}
              theme={theme}
            />
            <SettingsToggle
              label="Required"
              checked={field.required || false}
              onChange={(val) => onUpdate(index, { required: val })}
              palette={palette}
            />
            <SettingsRow>
              <SettingsInput
                label="Min repetitions"
                value={field.minRepetitions ?? ''}
                onChange={(v) => onUpdate(index, { minRepetitions: v === '' ? 1 : v })}
                placeholder="1"
                type="number"
                palette={palette}
                theme={theme}
              />
              <SettingsInput
                label="Max repetitions"
                value={field.maxRepetitions ?? ''}
                onChange={(v) => onUpdate(index, { maxRepetitions: v === '' ? null : v })}
                placeholder="Unlimited"
                type="number"
                palette={palette}
                theme={theme}
              />
            </SettingsRow>
          </Box>
        )}
      </Box>

      {/* Bottom toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '2px',
          py: '2px',
          opacity: showControls ? 1 : 0,
          height: showControls ? '24px' : 0,
          overflow: 'hidden',
          transition: 'opacity 0.15s ease, height 0.15s ease',
        }}
      >
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); setSettingsExpanded((v) => !v); }}
          sx={{
            p: '3px',
            color: settingsExpanded ? palette.textPrimary : palette.textSecondary,
            backgroundColor: settingsExpanded ? palette.border : 'transparent',
            borderRadius: '4px',
          }}
          title="Settings"
        >
          <SettingsIcon sx={{ width: 15, height: 15 }} />
        </IconButton>
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); setAddMenuAnchor(e.currentTarget); }}
          sx={{ p: '3px', color: palette.textSecondary }}
          title="Add field below"
        >
          <AddIcon sx={{ width: 15, height: 15 }} />
        </IconButton>
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); onDuplicate(index); }}
          sx={{ p: '3px', color: palette.textSecondary }}
          title="Duplicate"
        >
          <ContentCopyIcon sx={{ width: 14, height: 14 }} />
        </IconButton>
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); onRemove(index); }}
          sx={{ p: '3px', color: palette.textSecondary, '&:hover': { color: palette.errorText || '#b91c1c' } }}
          title="Delete"
        >
          <DeleteOutlineIcon sx={{ width: 15, height: 15 }} />
        </IconButton>
      </Box>

      {/* Add field below menu (top-level) */}
      <Menu
        anchorEl={addMenuAnchor}
        open={addMenuOpen}
        onClose={() => setAddMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{ paper: { sx: { maxWidth: 240, maxHeight: 360, borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', mt: '4px' } } }}
      >
        {FIELD_CATEGORIES.map((cat, catIdx) => [
          catIdx > 0 && <Divider key={`div-${catIdx}`} />,
          <Typography
            key={`cat-${cat.label}`}
            sx={{ px: '16px', pt: catIdx === 0 ? '8px' : '6px', pb: '4px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', userSelect: 'none' }}
          >
            {cat.label}
          </Typography>,
          ...cat.types.map((t) => {
            const Icon = t.icon;
            return (
              <MenuItem key={t.value} onClick={() => handleAddSelect(t.value)} sx={{ px: '16px', py: '6px', minHeight: 0, mx: '4px', borderRadius: '4px', '&:hover': { backgroundColor: 'action.hover' } }}>
                <ListItemIcon sx={{ minWidth: '28px !important' }}><Icon sx={{ width: 16, height: 16 }} /></ListItemIcon>
                <ListItemText primary={t.label} primaryTypographyProps={{ fontSize: '13px', fontWeight: 500 }} />
              </MenuItem>
            );
          }),
        ])}
      </Menu>

      {/* Add child field menu */}
      <Menu
        anchorEl={childAddMenuAnchor}
        open={childAddMenuOpen}
        onClose={() => setChildAddMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{ paper: { sx: { maxWidth: 240, maxHeight: 360, borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', mt: '4px' } } }}
      >
        {CHILD_FIELD_CATEGORIES.map((cat, catIdx) => [
          catIdx > 0 && <Divider key={`div-${catIdx}`} />,
          <Typography
            key={`cat-${cat.label}`}
            sx={{ px: '16px', pt: catIdx === 0 ? '8px' : '6px', pb: '4px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', userSelect: 'none' }}
          >
            {cat.label}
          </Typography>,
          ...cat.types.map((t) => {
            const Icon = t.icon;
            return (
              <MenuItem key={t.value} onClick={() => handleChildAddSelect(t.value)} sx={{ px: '16px', py: '6px', minHeight: 0, mx: '4px', borderRadius: '4px', '&:hover': { backgroundColor: 'action.hover' } }}>
                <ListItemIcon sx={{ minWidth: '28px !important' }}><Icon sx={{ width: 16, height: 16 }} /></ListItemIcon>
                <ListItemText primary={t.label} primaryTypographyProps={{ fontSize: '13px', fontWeight: 500 }} />
              </MenuItem>
            );
          }),
        ])}
      </Menu>
    </Box>
  );
}
